import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, Injector, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { CBox_GetPreInvoiceDataResponse, CBox_GetPreInvoiceDocumentDataResponse, CBox_GetPreInvoicesListDataParams, CBox_PreInvoiceListItem } from "@server/services/cbox/public/api/v1/resources/financial/pre_invoices/types";
import { CBox_GetLockerBaseData } from "@server/services/cbox/public/api/v1/resources/locker/types";
import { PaginatedTableWithFilters } from "app/shared/paginated-table-with-filters";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { MenuModule } from "primeng/menu";
import { ProgressBarModule } from "primeng/progressbar";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { first } from "rxjs";
import { lockerCleanAddress } from "src/helpers/functions";
import { ProfileService } from "src/services/profile/profile.service";

import { CBoxFinancePreInvoiceRejectDialogComponent } from "../dialogs/pre-invoices/reject/cbox-finance-pre-invoice-reject-dialog.component";
import { TagModule } from "primeng/tag";
import { MenuItem } from "primeng/api";
import { CBox_PublicSuccessResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { fileDownloadHelper } from "src/helpers/file-download";
import { CBoxFinancePreInvoiceApproveDialogComponent } from "../dialogs/pre-invoices/approve/cbox-finance-pre-invoice-approve-dialog.component";
import { SkeletonModule } from "primeng/skeleton";
import { CBox_InvoicingDocumentComputedMetadataKey } from "@server/services/cbox/common/invoicing/metadata/metadata_keys";
import { MultiSelectModule } from "primeng/multiselect";
import { handlePublicApiError } from "src/services/api/api.service";
import { DividerModule } from "primeng/divider";
import { InplaceModule } from "primeng/inplace";
import { CBoxFinancePreInvoiceResetDialogComponent } from "../dialogs/pre-invoices/reset/cbox-finance-pre-invoice-reset-dialog.component";

interface BoxTotals {
  count: number;
  discountCount: number;
  amount: number;
  discountAmount: number;
  finalAmount: number;
}

interface BoxRow {
  boxType: { name: string };
  total: { count: number; amount: number };
  discount: { count: number; amount: number };
  final: { amount: number };
}

@Component({
  selector: "app-cbox-profile-finance-pre-invoices-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    MenuModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    CalendarModule,
    MultiSelectModule,
    ProgressBarModule,
    TooltipModule,
    TagModule,
    MenuModule,
    SkeletonModule,
    DividerModule,
    InplaceModule
  ],
  templateUrl: "./cbox-profile-finance-pre-invoices-list.component.html",
  styleUrls: ["./cbox-profile-finance-pre-invoices-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CBoxProfileFinancePreInvoicesListComponent extends
  PaginatedTableWithFilters<CBox_PreInvoiceListItem, CBox_GetPreInvoicesListDataParams> {

  private fb = inject(FormBuilder);
  private profileService = inject(ProfileService);
  private dialog = inject(MatDialog);

  filtersForm: FormGroup = this.fb.group({
    page: [1],
    pageSize: [100],
    status: [["PENDING"]],
    interval: [[this.oneMonthAgo, this.today]],
    identifier: [""]
  });

  sessionFiltersKey = "finance/pre-invoices/filters";
  sessionFiltersToSave = ["page", "pageSize", "status", "identifier"];
  localStorageFiltersKey = "finance/pre-invoices/filters";
  localStorageFiltersToSave: string[] = [];

  defaultFilters = signal({
    page: 1,
    pageSize: 100,
    status: ["PENDING"],
    interval: [this.oneMonthAgo, this.today],
    identifier: ""
  });
  dataFetchUrl = "financial/pre-invoices";
  expandedRows = signal<Record<string, boolean>>({});

  statusesOptions = signal([
    { label: "In asteptare", value: "PENDING" },
    { label: "Acceptate", value: "APPROVED" },
    { label: "Refuzate", value: "REJECTED" }
  ]);

  statusesTranslationsList = signal<Record<string, string>>({
    APPROVED: "Aprobat",
    DELETED: "Șters",
    REJECTED: "Refuzat",
    APPROVED_PENDING: "Acceptat",
    CONVERTED: "Convertit în factură",
    PENDING_APPROVAL: "În așteptare acțiune",
    REJECTED_PENDING: "Refuzat"
  });

  private deadlines = signal(new Map<string, number>());
  preInvoicesMenuItems = signal<Record<string, MenuItem[]>>({});
  downloading = signal<Record<string, boolean>>({});

  preInvoicesData = signal<Record<CBox_PreInvoiceListItem["identifier"], CBox_GetPreInvoiceDataResponse>>({});
  preInvoiceMetadataKeys = CBox_InvoicingDocumentComputedMetadataKey;

  detailsToggled = signal<Record<string, boolean>>({});

  /** signal for manual update fallback (keep existing method) */
  remainingTimes = signal(
    new Map<
      string,
      { hours: number, minutes: number, full: string, hoursOnly: string }>());

  private now = signal(Date.now());


  /** computed remaining times map */
  remainingTimesMap = computed(() => {
    const now = this.now();
    const m = new Map<string, {
      hours: number,
      minutes: number,
      full: string,
      hoursOnly: string
    }>();
    this.deadlines().forEach((ts, id) => {
      const diff = ts - now;
      if (diff <= 0)
        m.set(id, {
          hours: 0,
          minutes: 0,
          full: "Expirat",
          hoursOnly: "Expirat"
        });
      else {
        const h = Math.floor(diff / 3_600_000);
        const mi = Math.floor((diff % 3_600_000) / 60_000);
        m.set(id, {
          hours: h,
          minutes: mi,
          full: `${h} ore si ${mi} minute`,
          hoursOnly: `${h} ore`
        });
      }
    });
    return m;
  });

  private intervalId!: number;

  constructor(inj: Injector) {
    super(inj);
  }

  async init() {
    this.profileService.setTitle("Borderouri facturi");
    this.intervalId = window.setInterval(
      () => this.now.set(Date.now()),
      1_000
    );
  }

  override peekData(items: CBox_PreInvoiceListItem[]) {
    this.registerDeadlines(items);
  }

  private registerDeadlines(items: CBox_PreInvoiceListItem[] = this.data()) {
    const nd = new Map(this.deadlines());
    items
      .filter(
        i => i.approval?.changeWindowEndsAt)
      .forEach(i => {
        if (i.approval?.changeWindowEndsAt) {
          const base = new Date(i.approval.changeWindowEndsAt).getTime();
          nd.set(i.identifier, base);
        }
      });
    this.deadlines.set(nd);
    this.updateRemainingTimes();
  }

  ngOnDestroy() {
    clearInterval(this.intervalId);
  }

  private updateRemainingTimes() {
    const now = Date.now();
    const m = new Map<string, { hours:number; minutes:number; full:string; hoursOnly:string }>();
    this.deadlines().forEach((ts, id) => {
      const diff = ts - now;
      if (diff <= 0) {
        m.set(id, { hours: 0, minutes: 0, full: 'Expirat', hoursOnly: 'Expirat' });
      } else {
        const h  = Math.floor(diff / 3_600_000);
        const mi = Math.floor((diff % 3_600_000) / 60_000);
        m.set(id, {
          hours: h,
          minutes: mi,
          full: `${h} ore si ${mi} minute`,
          hoursOnly: `${h} ore`
        });
      }
    });
    this.remainingTimes.set(m);
  }

  expandRow(preInvoice: CBox_PreInvoiceListItem) {
    if (!this.hasAccessTo([this.permissions.viewBillingPreInvoiceDetails])) {
      this.toastService.showErrorToast("Acces interzis", "Nu aveți permisiunea de a vizualiza detaliile acestui borderou.");
      return;
    }
    this.expandedRows.update(r => ({ ...r, [preInvoice.identifier]: true }));
    if (!this.preInvoicesData()[preInvoice.identifier]) {
      this.fetchPreInvoiceData(preInvoice);
    }
  }

  collapseRow(preInvoice: CBox_PreInvoiceListItem): void {
    this.expandedRows.update(r => {
      delete r[preInvoice.identifier];
      return {...r};
    });
    this.detailsToggled.update(toggled => {
      delete toggled[preInvoice.identifier];
      return { ...toggled };
    });
  }

  fetchPreInvoiceData(preInvoice: CBox_PreInvoiceListItem): void {
    this.api.get<CBox_PublicSuccessResponse<CBox_GetPreInvoiceDataResponse>>(`backend/financial/pre-invoice/${preInvoice.identifier}`).subscribe((response) => {
      console.log(response.data, "PRE INVOICE RESPONSE");
      this.preInvoicesData.update(r => ({ ...r, [preInvoice.identifier]: response.data }))
    }, (e) => {
      this.collapseRow(preInvoice);
      handlePublicApiError(e, this.toastService);
    });
  }

  formatAddress(a: CBox_GetLockerBaseData["addressParts"]): string {
    return lockerCleanAddress(a);
  }

  approve(preInvoice: CBox_PreInvoiceListItem): void {
    const ref = this.dialog.open(CBoxFinancePreInvoiceApproveDialogComponent, {
      data: preInvoice,
      width: "min(600px, 100%)"
    });
    ref.afterClosed().pipe(first()).subscribe(() => {
      this.collapseRow(preInvoice);
      this.preInvoicesData.update(data => {
        delete data[preInvoice.identifier];
        return { ...data };
      });
      this.fetchData();
    });
  }

  reject(preInvoice: CBox_PreInvoiceListItem): void {
    const ref = this.dialog.open(CBoxFinancePreInvoiceRejectDialogComponent, {
      data: preInvoice,
      width: "min(600px, 100%)"
    });
    ref.afterClosed().pipe(first()).subscribe(() => {
      this.collapseRow(preInvoice);
      this.preInvoicesData.update(data => {
        delete data[preInvoice.identifier];
        return { ...data };
      });
      this.fetchData();
    });
  }

  reset(preInvoice: CBox_PreInvoiceListItem): void {
    const ref = this.dialog.open(CBoxFinancePreInvoiceResetDialogComponent, {
      data: preInvoice,
      width: "min(600px, 100%)"
    });
    ref.afterClosed().pipe(first()).subscribe(() => {
      this.collapseRow(preInvoice);
      this.preInvoicesData.update(data => {
        delete data[preInvoice.identifier];
        return { ...data };
      });
      this.fetchData();
    });
  }

  isPastChangeWindow(end?: string): boolean {
    return !!end && Date.now() > new Date(end).getTime();
  }

  getStatusSeverity(status: CBox_PreInvoiceListItem["status"]): "success" | "info" | "warning" | "danger" | "secondary" | "contrast" {
    if (status === "APPROVED") {
      return "info";
    }

    if (status === "CONVERTED") {
      return "success";
    }

    if (status === "DELETED") {
      return "danger";
    }

    if (status === "REJECTED") {
      return "danger";
    }

    if (status === "APPROVED_PENDING") {
      return "info";
    }

    if (status === "PENDING_APPROVAL") {
      return "warning";
    }

    if (status === "REJECTED_PENDING") {
      return "danger";
    }

    return "secondary";
  }

  getPreInvoiceMenuItems(invoice: CBox_PreInvoiceListItem): MenuItem[] {
      const documents = invoice.documents;
      const systemPdf = documents.find((d) => d.format === "PDF" && d.source === "GENERATED_BY_SYSTEM");
      const einvoicingPdf = documents.find((d) => d.format === "PDF" && d.source === "GENERATED_BY_EINVOCING");
      const derivedPdf = documents.find((d) => d.format === "PDF" && d.source === "DERIVED_FROM_EINVOICING");

      const systemZip = documents.find((d) => d.format === "ZIP" && d.source === "GENERATED_BY_SYSTEM");
      const einvoicingZip = documents.find((d) => d.format === "ZIP" && d.source === "GENERATED_BY_EINVOCING");
      const derivedZip = documents.find((d) => d.format === "ZIP" && d.source === "DERIVED_FROM_EINVOICING");

      const systemXml = documents.find((d) => d.format === "XML" && d.source === "GENERATED_BY_SYSTEM");
      const einvoicingXml = documents.find((d) => d.format === "XML" && d.source === "GENERATED_BY_EINVOCING");
      const derivedXml = documents.find((d) => d.format === "XML" && d.source === "DERIVED_FROM_EINVOICING");

      const systemXlsx = documents.find((d) => d.format === "XLSX" && d.source === "GENERATED_BY_SYSTEM");
      const einvoicingXlsx = documents.find((d) => d.format === "XLSX" && d.source === "GENERATED_BY_EINVOCING");
      const derivedXlsx = documents.find((d) => d.format === "XLSX" && d.source === "DERIVED_FROM_EINVOICING");

      return [
        {
          label: "PDF",
          icon: "pi pi-file-pdf",
          visible: !!systemPdf,
          command: () => {
            this.downloadDocument(invoice, systemPdf!);
          },
        },
        {
          label: "PDF (generat de e-factura)",
          icon: "pi pi-file-pdf",
          visible: !!einvoicingPdf,
          command: () => {
            this.downloadDocument(invoice, einvoicingPdf!);
          },
        },
        {
          label: "PDF (generat din fișier e-factura)",
          icon: "pi pi-file-pdf",
          visible: !!derivedPdf,
          command: () => {
            this.downloadDocument(invoice, derivedPdf!);
          },
        },
        {
          label: "Arhivă",
          icon: "pi pi-file-plus",
          visible: !!systemZip,
          command: () => {
            this.downloadDocument(invoice, systemZip!);
          },
        },
        {
          label: "Arhivă (generat de e-factura)",
          icon: "pi pi-file-plus",
          visible: !!einvoicingZip,
          command: () => {
            this.downloadDocument(invoice, einvoicingZip!);
          },
        },
        {
          label: "Arhivă (generat din fișier e-factura)",
          icon: "pi pi-file-plus",
          visible: !!derivedZip,
          command: () => {
            this.downloadDocument(invoice, derivedZip!);
          },
        },
        {
          label: "XML",
          icon: "pi pi-file",
          visible: !!systemXml,
          command: () => {
            this.downloadDocument(invoice, systemXml!);
          },
        },
        {
          label: "XML (generat de e-factura)",
          icon: "pi pi-file",
          visible: !!einvoicingXml,
          command: () => {
            this.downloadDocument(invoice, einvoicingXml!);
          },
        },
        {
          label: "XML (generat din fișier e-factura)",
          icon: "pi pi-file",
          visible: !!derivedXml,
          command: () => {
            this.downloadDocument(invoice, derivedXml!);
          },
        },
        {
          label: "Excel",
          icon: "pi pi-file-excel",
          visible: !!systemXlsx,
          command: () => {
            this.downloadDocument(invoice, systemXlsx!);
          },
        },
        {
          label: "Excel (generat de e-factura)",
          icon: "pi pi-file-excel",
          visible: !!einvoicingXlsx,
          command: () => {
            this.downloadDocument(invoice, einvoicingXlsx!);
          },
        },
        {
          label: "Excel (generat din fișier e-factura)",
          icon: "pi pi-file-excel",
          visible: !!derivedXlsx,
          command: () => {
            this.downloadDocument(invoice, derivedXlsx!);
          },
        },
      ];
    }

  menuClosed(preInvoice: CBox_PreInvoiceListItem): void {
    this.preInvoicesMenuItems.update(rows => {
      delete rows[preInvoice.identifier];
      return rows;
    });
  }

  downloadDocument(
      invoice: CBox_PreInvoiceListItem,
      document: CBox_PreInvoiceListItem["documents"][number]
    ) {
      try {
        this.setAsDownloading(invoice);
        this.api
          .get<CBox_PublicSuccessResponse<CBox_GetPreInvoiceDocumentDataResponse>>(
            `backend/financial/invoices/document/${document?.identifier}`
          )
          .subscribe(
            (response) => {
              if (!response.data.data) {
                this.setAsNotDownloading(invoice);
                this.toastService.showErrorToast(
                  "Eroare",
                  "Eroare la descărcarea documentului!"
                );
                return;
              }
              fileDownloadHelper(
                { data: (response.data as any).data.data },
                document.format.toLowerCase(),
                response.data.fileName
              );
              this.setAsNotDownloading(invoice);
              this.toastService.showSuccessToast(
                "Confirmare",
                "Fișierul a fost descărcat cu succes!"
              );
            }, () => {
              this.setAsNotDownloading(invoice);
              this.toastService.showErrorToast(
                "Eroare",
                "A apărut o eroare la descărcarea fișierului!"
              );
            }
          );
      } catch (e) {
        this.setAsNotDownloading(invoice);
      }
    }

  getBoxesTotal(preInvoiceData: CBox_GetPreInvoiceDataResponse, metadataKey: keyof CBox_GetPreInvoiceDataResponse["metadata"]): number {
    const preInvoiceMetadataKeys = this.preInvoiceMetadataKeys;
    if (!preInvoiceData.metadata || !preInvoiceData.metadata[preInvoiceMetadataKeys.BOXES_COUNTS_BY_TYPE]) {
      return 0;
    }
    return preInvoiceData.metadata[preInvoiceMetadataKeys.BOXES_COUNTS_BY_TYPE]!.reduce((acc, curr) => acc + curr.total.count, 0);
  }

  getSearchStructure(): Record<string, unknown> {
    let interval = this.filtersForm.value.interval;
    if (typeof interval === "string") interval = interval.split(",");
    return Object
      .entries({
        page: this.filtersForm.value.page,
        pageSize: this.filtersForm.value.pageSize,
        afterDate: interval[0] ? new Date(interval[0]).toISOString() : this.today,
        beforeDate: interval[1] ? new Date(interval[1]).toISOString() : this.oneMonthAgo,
        "status[]": this.filtersForm.value.status,
        identifier: this.filtersForm.value.identifier,
      })
      .filter(
        ([, v]) => Array.isArray(v) ? v.length > 0 :
          v != null && (v as any).toString())
      .reduce((a, [k, v]) => (a[k] = v, a), {} as Record<string, unknown>);
  }

  getTotalWithoutVAT(preInvoiceData: CBox_GetPreInvoiceDataResponse): number {
    const items = preInvoiceData.items;
    return items.reduce((total, item) => total + (item.unitPrice * item.quantity), 0);
  }

  getTotalVAT(preInvoiceData: CBox_GetPreInvoiceDataResponse): number {
    const items = preInvoiceData.items;
    return items.reduce((total, item) => total + item.taxAmount, 0);
  }

  getTotalToPay(preInvoiceData: CBox_GetPreInvoiceDataResponse): number {
    const items = preInvoiceData.items;
    return items.reduce((total, item) => total + (item.unitPrice * item.quantity) + item.taxAmount, 0);
  }

  getItemTotal(item: CBox_GetPreInvoiceDataResponse["items"][number]): number {
    return (item.unitPrice * item.quantity) + item.taxAmount;
  }

  toggleDetailsFor(preInvoice: CBox_PreInvoiceListItem): void {
    this.detailsToggled.update(toggled => {
      toggled[preInvoice.identifier] = !toggled[preInvoice.identifier];
      return { ...toggled };
    });
  }

  calculateBoxesTotal(boxes: CBox_GetPreInvoiceDataResponse["metadata"]["boxes_counts_by_type"]): BoxTotals {
    return boxes?.reduce(
      (acc, r) => ({
        count: acc.count + r.total.count,
        discountCount: acc.discountCount + r.discount.count,
        amount: acc.amount + r.total.amount,
        discountAmount: acc.discountAmount + r.discount.amount,
        finalAmount: acc.finalAmount + r.final.amount,
      }),
      { count: 0, discountCount: 0, amount: 0, discountAmount: 0, finalAmount: 0 }
    ) ?? { count: 0, discountCount: 0, amount: 0, discountAmount: 0, finalAmount: 0 };
  }

  private setAsDownloading(preInvoice: CBox_PreInvoiceListItem): void {
    this.downloading.update(downloadingItems => {
      downloadingItems[preInvoice.identifier] = true;
      return downloadingItems;
    });
  }

  private setAsNotDownloading(preInvoice: CBox_PreInvoiceListItem): void {
    this.downloading.update(downloadingItems => {
      delete downloadingItems[preInvoice.identifier];
      return { ...downloadingItems };
    });
  }
}
